import { useCallback } from 'react';
import {
  useForm,
  useFormState,
  UseFormProps,
  UseFormReturn,
  FieldValues,
  SubmitHandler,
  SubmitErrorHandler,
} from 'react-hook-form';
import { pick } from 'lodash';

interface UseUpdateFormReturn<T extends FieldValues> extends UseFormReturn<T> {
  // Add a new method for dirty field submission while keeping handleSubmit compatible
  handleUpdateSubmit: (
    onValid: SubmitHandler<Partial<T>>,
    onInvalid?: SubmitErrorHandler<T>,
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
}

/**
 * Enhanced useForm hook that extends React Hook Form with automatic dirty field tracking.
 * Uses React Hook Form's built-in dirtyFields and validation.
 * The handleSubmit function only passes changed fields to the submit handler.
 */
export const useUpdateForm = <T extends FieldValues>(
  options: UseFormProps<T>,
): UseUpdateFormReturn<T> => {
  const form = useForm<T>(options);
  const { dirtyFields } = useFormState({
    control: form.control,
  });

  /**
   * Enhanced handleUpdateSubmit that only passes dirty fields to the submit handler
   */
  const handleUpdateSubmit = useCallback(
    (onValid: SubmitHandler<Partial<T>>, onInvalid?: SubmitErrorHandler<T>) => {
      return form.handleSubmit(async (data: T) => {
        const dirtyFieldKeys = Object.keys(dirtyFields).filter(
          (key) => dirtyFields[key as keyof typeof dirtyFields],
        );

        const changedFields = pick(data, dirtyFieldKeys) as Partial<T>;

        await onValid(changedFields);
      }, onInvalid);
    },
    [form, dirtyFields],
  );

  return {
    ...form,
    handleUpdateSubmit,
  };
};
